import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";

import { useWorkspacePersistHydrated } from "@/hooks/useWorkspacePersistHydrated";
import { WorkspaceRequiredEmptyState } from "@/routes/WorkspaceRequiredEmptyState";
import {
  isWorkspaceUrlBootstrapPath,
  workspaceIdFromPathname,
} from "@/routes/workspaceBootstrapPaths";
import { useWorkspaceStore } from "@/state/workspace.store";

/**
 * Route gate for workspace-scoped surfaces.
 *
 * When no workspace is selected: render an in-page empty state with a picker
 * (HomeEmptyState pattern). Do NOT redirect to /inbox — the user stays on the
 * URL they opened and learns what is missing.
 *
 * Zustand persist hydration: wait via useWorkspacePersistHydrated before
 * deciding. URL-bearing deep links (/workspaces/:id/*, /projects/:id, /w/:slug)
 * bootstrap from the route and must not be blocked by a missing store id.
 */
export default function RequireWorkspace() {
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const setActiveWorkspace = useWorkspaceStore((s) => s.setActiveWorkspace);
  const hydrated = useWorkspacePersistHydrated();
  const location = useLocation();

  const urlWorkspaceId = workspaceIdFromPathname(location.pathname);
  const isBootstrap = isWorkspaceUrlBootstrapPath(location.pathname);

  useEffect(() => {
    if (!hydrated || !urlWorkspaceId) return;
    if (urlWorkspaceId === activeWorkspaceId) return;
    setActiveWorkspace(urlWorkspaceId);
  }, [hydrated, urlWorkspaceId, activeWorkspaceId, setActiveWorkspace]);

  if (!hydrated) {
    return (
      <div
        className="flex h-64 items-center justify-center"
        data-testid="require-workspace-hydrating"
      >
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  if (!activeWorkspaceId && !isBootstrap && !urlWorkspaceId) {
    return <WorkspaceRequiredEmptyState />;
  }

  return <Outlet />;
}
